<template>
    <div class="field pa-field">
        <label :for="inputId">{{ label }}</label>
        <div class="pa-wrap">
            <svg class="ico pa-icon" aria-hidden="true"><use href="#i-magnifying-glass"></use></svg>
            <input
                :id="inputId"
                type="text"
                v-model="text"
                :placeholder="placeholder"
                autocomplete="off"
                @input="onInput"
                @focus="openList"
                @blur="closeSoon"
                @keydown.down.prevent="move(1)"
                @keydown.up.prevent="move(-1)"
                @keydown.enter.prevent="chooseActive"
                @keydown.esc="open = false"
            >
            <button v-if="modelValue" type="button" class="pa-clear" aria-label="Clear" @mousedown.prevent="clear">
                <svg class="ico" aria-hidden="true"><use href="#i-xmark"></use></svg>
            </button>
            <ul v-if="open && (flat.length || otherRoads.length)" class="pa-list" role="listbox">
                <template v-for="group in groups" :key="group.code">
                    <li class="pa-group" role="presentation">{{ group.code }} — {{ group.name }}</li>
                    <li v-for="p in group.points" :key="p.id" role="option" :aria-selected="p.flatIndex === active">
                        <button type="button" :class="{ active: p.flatIndex === active }" @mousedown.prevent="select(p)">
                            <span>{{ p.name }}</span>
                            <span class="pa-chip">{{ group.code }}</span>
                        </button>
                    </li>
                </template>

                <!-- Matches on a different expressway. Showing these as a dead
                     "no results" was the Mamplasan bug: the point exists, it
                     just is not on the road already chosen. -->
                <template v-if="otherRoads.length">
                    <li class="pa-group pa-group-other" role="presentation">
                        On another expressway — tap to switch
                    </li>
                    <li v-for="p in otherRoads" :key="'x' + p.id" role="option">
                        <button type="button" class="pa-other" @mousedown.prevent="switchRoad(p)">
                            <span>{{ p.name }}</span>
                            <span class="pa-chip pa-chip-other">{{ p.code }}</span>
                        </button>
                    </li>
                </template>
            </ul>
            <p v-else-if="open && query && !flat.length" class="pa-empty">No toll point matches that name.</p>
        </div>
    </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';

const props = defineProps({
    modelValue: { type: Object, default: null },   // { id, name, expresswayId, code }
    label: { type: String, required: true },
    inputId: { type: String, required: true },
    placeholder: { type: String, default: 'Type a toll point or city…' },
    points: { type: Array, required: true },        // flat: {id, name, expresswayId, code, expresswayName}
    restrictExpresswayId: { type: Number, default: null },
    excludeId: { type: Number, default: null },
});

const emit = defineEmits(['update:modelValue', 'switch-road']);

const text = ref(props.modelValue?.name ?? '');
const open = ref(false);
const active = ref(-1);
const query = ref('');

watch(() => props.modelValue, (val) => {
    text.value = val?.name ?? '';
    if (val) query.value = '';
});

const available = computed(() => props.points.filter((p) =>
    (props.restrictExpresswayId === null || p.expresswayId === props.restrictExpresswayId)
    && p.id !== props.excludeId
));

const filtered = computed(() => {
    const q = query.value.trim().toLowerCase();
    const source = q
        ? available.value.filter((p) => p.name.toLowerCase().includes(q) || p.code.toLowerCase().includes(q))
        : available.value;
    return source.slice(0, 40);
});

// Group by expressway, preserving point order; assign flat indexes for keyboard nav.
const groups = computed(() => {
    const byCode = new Map();
    let flatIndex = 0;
    for (const p of filtered.value) {
        if (!byCode.has(p.code)) {
            byCode.set(p.code, { code: p.code, name: p.expresswayName, points: [] });
        }
        byCode.get(p.code).points.push({ ...p, flatIndex: flatIndex++ });
    }
    return [...byCode.values()];
});

const flat = computed(() => groups.value.flatMap((g) => g.points));

/**
 * Points matching the query that are hidden only because the OTHER field has
 * pinned the search to one expressway. Alabang exists on both SLEX and Skyway,
 * so picking the wrong one used to make every SLEX exit disappear with no way
 * back except clearing the form.
 */
const otherRoads = computed(() => {
    const q = query.value.trim().toLowerCase();

    if (!q || props.restrictExpresswayId === null) return [];

    return props.points
        .filter((p) => p.expresswayId !== props.restrictExpresswayId
            && (p.name.toLowerCase().includes(q) || p.code.toLowerCase().includes(q)))
        .slice(0, 6);
});

function switchRoad(p) {
    open.value = false;
    active.value = -1;
    // The parent owns both ends, so it decides what happens to the other one.
    emit('switch-road', { id: p.id, name: p.name, expresswayId: p.expresswayId, code: p.code });
}

const restrictNote = computed(() => {
    if (props.restrictExpresswayId === null) return '';
    const code = props.points.find((p) => p.expresswayId === props.restrictExpresswayId)?.code;
    return code ? ` on ${code} (clear the other field to search all expressways)` : '';
});

function onInput() {
    query.value = text.value;
    active.value = -1;
    open.value = true;
    if (props.modelValue && text.value !== props.modelValue.name) {
        emit('update:modelValue', null);
    }
}

function openList() {
    query.value = props.modelValue ? '' : text.value;
    open.value = true;
}

function closeSoon() {
    setTimeout(() => {
        open.value = false;
        // Restore the chosen name if the user typed junk then left.
        text.value = props.modelValue?.name ?? text.value;
    }, 150);
}

function move(dir) {
    if (!open.value || !flat.value.length) return;
    active.value = (active.value + dir + flat.value.length) % flat.value.length;
}

function chooseActive() {
    if (open.value && active.value >= 0 && flat.value[active.value]) {
        select(flat.value[active.value]);
    } else if (flat.value.length === 1) {
        select(flat.value[0]);
    }
}

function select(p) {
    emit('update:modelValue', { id: p.id, name: p.name, expresswayId: p.expresswayId, code: p.code });
    open.value = false;
    active.value = -1;
}

function clear() {
    emit('update:modelValue', null);
    text.value = '';
    query.value = '';
    open.value = true;
}
</script>
